import { accountTypes, rpsToAccountTypes } from '../shared/utils';
import { modalOpenHandler } from '../../components/Modal/utils';
import {
  isEmptyArray,
  isEmptyObject,
  navigateToExternal
} from '../../core/util';
import config from '../../config';

const isRothOrTraditional = (chosen: string): boolean => {
  return chosen.includes('traditional') || chosen.includes('roth');
};
const isTransferIra = (chosen: string): boolean => {
  return chosen.includes('transfer') && chosen.includes('ira');
};

export const pushToBrokerageDelegateUser = (
  chosen: string,
  customerID: string
): void => {
  const brokerageAccountUrl = `${config(
    'internalReturnUrlPrefix'
  )}personal-investing/new-account/setup/account-info/brk-checklist/${chosen}?delegateUser=${customerID}`;
  navigateToExternal(brokerageAccountUrl);
};

const pushToInProgressAppModal = (
  inProgressApps: Array<any>,
  loadedData: any
): any => {
  // prioritize SAVE FOR LATER (associate started) over IN PROGRESS (customer started)
  const saveForLaterApp = inProgressApps.find((app: any) =>
    app?.consumerName.includes('FRONT_OFFICE')
  );

  // Warn of in-progress applications that the client may have before deleting/overwritting
  modalOpenHandler('appInProgressModal');
  return {
    loadedData: {
      ...loadedData,
      inprogressApplication: !isEmptyObject(saveForLaterApp)
        ? saveForLaterApp
        : inProgressApps[0]
    }
  };
};

export const isUserNamePresent = (loadedData): boolean =>
  Boolean(loadedData?.customerId);

export const brkAccountSelectionToAccountType = (
  brkAccountSelection: string
): string => {
  const brkPrefix = 'BROKERAGE_';
  const brkAccountType = {
    individual: accountTypes.INDIVIDUAL,
    joint: accountTypes.JOINT,
    'rollover-ira': accountTypes.ROLLOVER,
    'roth-ira': accountTypes.CONTRIBUTORY,
    'roth-rollover-ira': accountTypes.ROTH_ROLLOVER,
    'traditional-ira': accountTypes.CONTRIBUTORY,
    'transfer-individual': accountTypes.TRANSFER_INDIVIDUAL,
    'transfer-joint': accountTypes.TRANSFER_JOINT,
    'transfer-ugma-utma': accountTypes.TRANSFER_UGMAUTMA,
    'transfer-rollover-ira': accountTypes.TRANSFER,
    'transfer-roth-ira': accountTypes.TRANSFER,
    'transfer-traditional-ira': accountTypes.TRANSFER,
    'ugma-utma': accountTypes.UGMAUTMA
  }[brkAccountSelection];

  return `${brkPrefix}${brkAccountType}`;
};

const filterAppsForSecondaryAccountType = (
  chosen: string,
  loadedData: any,
  matchedInProgressApps: Array<any> = []
): any => {
  const toAccount = {
    'roth-ira': rpsToAccountTypes.ROTH,
    'traditional-ira': rpsToAccountTypes.TRADITIONAL,
    'transfer-rollover-ira': rpsToAccountTypes.ROLLOVER,
    'transfer-roth-ira': rpsToAccountTypes.ROTH,
    'transfer-traditional-ira': rpsToAccountTypes.TRADITIONAL
  };

  const appsMatchedByToAccountType = matchedInProgressApps?.filter(
    (application: any) => application?.toAccountType === toAccount[chosen]
  );

  // if no apps match the secondary account type, push to brokerage
  if (isEmptyArray(appsMatchedByToAccountType)) {
    return pushToBrokerageDelegateUser(chosen, loadedData?.customerId);
  }

  return pushToInProgressAppModal(appsMatchedByToAccountType, loadedData);
};

export const fetchAndfilterAppsForDelegateUser = async (
  chosen: string,
  loadedData: any,
  inProgressApps: Array<any> = []
): Promise<any> => {
  const matchedApplications = inProgressApps.filter(
    (rjApplication: any) =>
      rjApplication?.accountType === brkAccountSelectionToAccountType(chosen)
  );

  // If no existing applications match, proceed to push to brokerage
  if (isEmptyArray(matchedApplications)) {
    return pushToBrokerageDelegateUser(chosen, loadedData?.customerId);
  }

  // account for the secondary account type for Contributory and Transfer IRA accounts
  if (isTransferIra(chosen) || isRothOrTraditional(chosen)) {
    return filterAppsForSecondaryAccountType(
      chosen,
      loadedData,
      matchedApplications
    );
  }

  return pushToInProgressAppModal(matchedApplications, loadedData);
};
